import { BaseHandler } from './base-handler';
import { TaskResponse } from '../task-executor';
import { useStore } from '../../../store/useStore';
import { trackLead } from '../memory-store';

/**
 * Handles all task-related AI actions: Task creation and listing.
 */
export class TaskHandler extends BaseHandler {
  intent = 'task_action';

  async execute(params: any): Promise<TaskResponse> {
    const { action, title, dueDate, priority, leadId, name } = params;
    console.log(`[🤖 TASK HANDLER] Executing action: ${action}`, params);

    switch (action) {
      case 'create_task':
      case 'add_task':
        return this.createTask(title, dueDate, priority, leadId || name);
      case 'list_tasks':
      case 'show_tasks':
      case 'tasks_due':
      case 'task_query':
        return this.listTasks();
      case 'complete_task':
        return this.completeTask(title);
      default:
        console.warn(`[🤖 TASK HANDLER] Unhandled action: ${action}`);
        return this.wrapError(`Unknown task action: ${action}`);
    }
  }

  private parseNaturalDate(d: string): string {
    if (!d) return new Date().toISOString().split('T')[0];
    const lower = d.toLowerCase();
    const now = new Date();

    if (lower.includes('tomorrow')) {
      now.setDate(now.getDate() + 1);
    } else if (lower.includes('next week')) {
      now.setDate(now.getDate() + 7);
    } else if (lower.includes('monday')) {
      this.setNextDay(now, 1);
    } else if (lower.includes('tuesday')) {
      this.setNextDay(now, 2);
    } else if (lower.includes('wednesday')) {
      this.setNextDay(now, 3);
    } else if (lower.includes('thursday')) {
      this.setNextDay(now, 4);
    } else if (lower.includes('friday')) {
      this.setNextDay(now, 5);
    } else if (lower.includes('saturday')) {
      this.setNextDay(now, 6);
    } else if (lower.includes('sunday')) {
      this.setNextDay(now, 0);
    }

    return now.toISOString().split('T')[0];
  }

  private setNextDay(date: Date, dayOfWeek: number) {
    const resultDate = new Date(date.getTime());
    resultDate.setDate(date.getDate() + (7 + dayOfWeek - date.getDay()) % 7);
    if (resultDate <= date) resultDate.setDate(resultDate.getDate() + 7);
    date.setTime(resultDate.getTime());
  }

  private async createTask(title: string, dueDate: string, priority: string, leadIdentifier: string): Promise<TaskResponse> {
    const store = useStore.getState();
    const query = leadIdentifier?.toLowerCase();
    const lead = query ? store.leads.find(l => 
      l.id === leadIdentifier || 
      l.name.toLowerCase().includes(query) ||
      (l.propertyAddress && l.propertyAddress.toLowerCase().includes(query))
    ) : null;
    
    if (lead) trackLead(lead.id, lead.name);

    if (!title) return this.wrapError("I need a title for the task.");

    store.addTask({
      title,
      description: `Auto-generated for ${lead?.name || 'General'}`,
      status: 'todo',
      priority: (priority?.toLowerCase() as any) || 'medium',
      dueDate: this.parseNaturalDate(dueDate),
      assignedTo: store.currentUser?.id || 'system',
      createdBy: store.currentUser?.id || 'system',
      leadId: lead?.id
    } as any);

    return this.wrapSuccess(`Added task "${title}" to your list${lead ? ` for ${lead.name}` : ''}.`);
  }

  private async listTasks(): Promise<TaskResponse> {
    const store = useStore.getState();
    const pending = store.tasks.filter(t => t.status === 'todo');
    
    if (pending.length === 0) return this.wrapSuccess("Your task list is clear!");
    
    return this.wrapSuccess(`You have ${pending.length} pending tasks.`, { tasks: pending.slice(0, 5) });
  }

  private async completeTask(title: string): Promise<TaskResponse> {
    if (!title) return this.wrapError("Which task should I mark as complete?");
    const store = useStore.getState();
    const task = store.tasks.find(t => t.title.toLowerCase().includes(title.toLowerCase()));
    
    if (!task) return this.wrapError(`Couldn't find task: "${title}"`);
    
    store.updateTask(task.id, { status: 'done' });
    return this.wrapSuccess(`Marked task "${task.title}" as complete.`);
  }
}
